import React, { useState, useEffect, useMemo } from "react";
import { useEscapeClose } from "../../../utils/SamuiUtils";
import { useModalKeyboardNavigation, getKeyboardInstructions } from "../../../utils/ModalKeyboardNavigation";

const getItemUniqueKey = (item) =>
  item?.Item_Id ?? item?.Item_Code?.toString().trim().toLowerCase();

const ItemModal = ({
  formDetailList,
  showItemModal,
  handleItemClose,
  itemDataList,
  onRowSelectItem,
  deptDataList,
  purchaseType,
  selectedDeptInModal,
  setSelectedDeptInModal,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItemDataList, setFilteredItemDataList] =
    useState(itemDataList);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0); // เพิ่ม state สำหรับเลือกแถว

  const uniqueItemDataList = useMemo(() => {
    const itemMap = new Map();

    itemDataList.forEach((item) => {
      const itemKey = getItemUniqueKey(item);
      if (itemKey && !itemMap.has(itemKey)) {
        itemMap.set(itemKey, item);
      }
    });

    return Array.from(itemMap.values());
  }, [itemDataList]);

  const filteredItemKeys = useMemo(
    () => new Set(filteredItemDataList.map((item) => getItemUniqueKey(item))),
    [filteredItemDataList]
  );

  const isItemSelected = (item) =>
    selectedItems.some(
      (selected) => getItemUniqueKey(selected) === getItemUniqueKey(item)
    );

  const areAllFilteredItemsSelected =
    filteredItemDataList.length > 0 &&
    filteredItemDataList.every((item) => isItemSelected(item));

  const getSelectedItemsForConfirm = (highlightedItem) => {
    if (!highlightedItem || isItemSelected(highlightedItem)) {
      return selectedItems;
    }

    return [...selectedItems, highlightedItem];
  };

  useEscapeClose(showItemModal, handleItemClose);

  // Custom handler สำหรับ ItemModal
  const customSpaceHandler = () => {
    if (filteredItemDataList[selectedRowIndex]) {
      handleCheckboxChange(filteredItemDataList[selectedRowIndex]);
    }
  };

  // ใช้ Modal Keyboard Navigation Utility
  useModalKeyboardNavigation(
    showItemModal,
    filteredItemDataList,
    selectedRowIndex,
    setSelectedRowIndex,
    null, // ไม่ใช้ default onRowSelect
    handleItemClose,
    {
      customEnterHandler: (highlightedItem) =>
        onRowSelectItem(getSelectedItemsForConfirm(highlightedItem)),
      customSpaceHandler,
      enableSpace: true,
      includeHighlightedOnEnter: true,
      selectedItems: selectedItems
    }
  );

  useEffect(() => {
    if (showItemModal) {
      setSearchTerm("");
      setSelectedItems([]);
      setSelectedRowIndex(0); // รีเซ็ตการเลือกแถวเมื่อเปิด modal
    }
  }, [showItemModal]);

  useEffect(() => {
    const nextFilteredItemDataList = uniqueItemDataList.filter(
        (item) =>
          item.Item_Code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.Item_Name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredItemDataList(nextFilteredItemDataList);
    setSelectedRowIndex(0); // รีเซ็ตการเลือกแถวเมื่อข้อมูลเปลี่ยน
  }, [searchTerm, uniqueItemDataList]);

  // เพิ่ม keyboard navigation สำหรับ modal
  // เอา code ออก เพราะใช้ utility แทน

  // useEffect(() => {
  //     if (showItemModal) {
  //         setSelectedItems(
  //             formDetailList.map((item) =>
  //                 itemDataList.find((filtered) => filtered.Item_Id === item.itemId)
  //             ).filter(Boolean)
  //         );
  //     }
  // }, [showItemModal, formDetailList, itemDataList]);

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      setSelectedItems((prevSelected) => {
        const selectedKeySet = new Set(
          prevSelected.map((item) => getItemUniqueKey(item))
        );
        const itemsToAdd = filteredItemDataList.filter(
          (item) => !selectedKeySet.has(getItemUniqueKey(item))
        );

        return [...prevSelected, ...itemsToAdd];
      });
    } else {
      setSelectedItems((prevSelected) =>
        prevSelected.filter(
          (item) => !filteredItemKeys.has(getItemUniqueKey(item))
        )
      );
    }
  };

  const handleCheckboxChange = (item) => {
    setSelectedItems((prevSelected) => {
      const isSelected = prevSelected.some(
        (selected) => getItemUniqueKey(selected) === getItemUniqueKey(item)
      );

      const newSelectedItems = isSelected
        ? prevSelected.filter(
          (selected) => getItemUniqueKey(selected) !== getItemUniqueKey(item)
        )
        : [...prevSelected, item];

      // อัปเดต selectedRowIndex เฉพาะเมื่อ item ถูกเลือก (checked)
      if (!isSelected) { // ถ้าเป็นการเลือก (ไม่ใช่การยกเลิก)
        const itemIndex = filteredItemDataList.findIndex(
          filteredItem => getItemUniqueKey(filteredItem) === getItemUniqueKey(item)
        );
        if (itemIndex !== -1) {
          setSelectedRowIndex(itemIndex);
        }
      }

      return newSelectedItems;
    });
  };

  return (
    <>
      <div
        className={`modal ${showItemModal ? "show" : ""}`}
        style={{ display: showItemModal ? "block" : "none" }}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">รายชื่อสินค้า</h5>
              <button type="button" className="close" onClick={handleItemClose}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <div className="row">
                  <div className={
                    (window.location.pathname === '/purchase-request' && purchaseType === "2") ||
                      (window.location.pathname === '/payment-voucher')
                      ? "col-8" : "col-12"
                  }>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="ค้นหา รหัสสินค้า หรือ ชื่อสินค้า "
                      value={searchTerm || ""}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  {((window.location.pathname === '/purchase-request' && purchaseType === "2") ||
                    (window.location.pathname === '/payment-voucher')) && (
                      <div className="col-4">
                        <select
                          className="form-select form-control"
                          value={selectedDeptInModal || ''}
                          onChange={(e) => setSelectedDeptInModal(e.target.value)}
                        >
                          <option value="">เลือกแผนก</option>
                          {deptDataList && deptDataList.map((dept) => (
                            <option key={dept.Dept_Id} value={dept.Dept_Id}>
                              {dept.Dept_Name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                </div>
              </div>
              <div
                className="table-responsive"
                style={{ maxHeight: "650px", overflowY: "auto" }}
              >
                <table className="table table-striped table-hover">
                  <thead className="thead-dark">
                    <tr>
                      <th className="text-center" style={{ width: "5%" }}>
                        <input
                          type="checkbox"
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          checked={areAllFilteredItemsSelected}
                        />
                      </th>
                      <th className="text-center" style={{ width: "20%" }}>
                        รหัสสินค้า
                      </th>
                      <th className="text-center" style={{ width: "55%" }}>
                        ชื่อสินค้า
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItemDataList.length > 0 ? (
                      filteredItemDataList.map((item, index) => (
                        <tr
                          key={`${getItemUniqueKey(item) || "item"}-${index}`}
                          onClick={() => handleCheckboxChange(item)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: index === selectedRowIndex
                              ? "rgba(105, 188, 216, 0.8)"
                              : isItemSelected(item)
                                ? "rgba(105, 188, 216, 0.3)"
                                : "transparent",
                            transition: "background-color 0.3s ease",
                          }}
                        >
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={isItemSelected(item)}
                              onChange={() => handleCheckboxChange(item)}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                cursor: "pointer",
                                backgroundColor: isItemSelected(item)
                                  ? "rgb(105, 188, 216)"
                                  : "transparent",
                                transition: "background-color 0.3s ease",
                              }}
                            />
                          </td>
                          <td className="text-center">{item.Item_Code}</td>
                          <td className="text-left">{item.Item_Name}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center">
                          ไม่พบข้อมูล
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <div className="text-left" style={{ fontSize: "12px", color: "#666", marginRight: "auto" }}>
                {getKeyboardInstructions("สินค้า", true)}
              </div>
              <button
                className="btn btn-primary"
                onClick={() => onRowSelectItem(selectedItems)}
              //disabled={selectedItems.length === 0} // คอมเม้นไว้ก่อน ใช้สำหรับ Disabled เมื่อยังไม่ได้เลือกสินค้า
              >
                ยืนยัน (Enter)
              </button>
              <button className="btn btn-danger" onClick={handleItemClose}>
                ปิด (ESC)
              </button>
            </div>
          </div>
        </div>
      </div>
      {showItemModal && <div className="modal-backdrop fade show"></div>}
    </>
  );
};

export default ItemModal;
